// ═══════════════════════════════════════════════════════════════════
//  LAB PORTAL — public, token-gated page used by the external lab.
//  URL: /lab-portal/<portalToken>
//  No login — the token IS the access key. See server/routes/labPortalRoutes.js
// ═══════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = '/api/lab-portal';

export default function LabPortalPage() {
  const token = (window.location.pathname.match(/^\/lab-portal\/([^/]+)/) || [])[1] || '';
  const [me, setMe]       = useState(null);
  const [cases, setCases] = useState([]);
  const [activeCase, setActiveCase] = useState(null);  // { order, messages }
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const loadCases = async () => {
    try {
      const r = await axios.get(`${API}/${token}/cases`);
      setCases(r.data.rows || []);
      if (!me) {
        try {
          const m = await axios.get(`${API}/${token}/me`);
          setMe(m.data);
        } catch {}
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Invalid or revoked portal link');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!token) { setError('No portal token'); setLoading(false); return; }
    loadCases();
    const i = setInterval(loadCases, 15000);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [token]);

  if (loading) return <div style={centered}>Loading…</div>;
  if (error)   return <div style={centered}><div style={{textAlign:'center'}}><h2 style={{color:'#dc2626'}}>⚠️ {error}</h2><p style={{color:'#6b7280'}}>Please ask the clinic to send you a fresh portal link.</p></div></div>;

  return (
    <div style={shell}>
      <header style={hdr}>
        <div>
          <div style={{fontSize:12, color:'#9ca3af', fontWeight:600, textTransform:'uppercase'}}>Lab Portal · ORALZY</div>
          <div style={{fontSize:18, fontWeight:700, color:'white'}}>{me?.vendorName || 'Loading…'}</div>
        </div>
        <div style={{fontSize:11, color:'#cbd5e1'}}>Private link — do not share</div>
      </header>

      {activeCase ? (
        <CaseDetail token={token} caseId={activeCase} onBack={() => { setActiveCase(null); loadCases(); }}/>
      ) : (
        <CaseList cases={cases} onOpen={(id) => setActiveCase(id)}/>
      )}
    </div>
  );
}

function CaseList({ cases, onOpen }) {
  if (!cases.length) {
    return <div style={{padding:40, textAlign:'center', color:'#6b7280'}}>No open cases right now.</div>;
  }
  const statusColor = {
    pending:       '#fbbf24',
    sent:          '#f59e0b',
    'in-progress': '#3b82f6',
    received:      '#10b981',
    delivered:     '#6b7280',
  };
  return (
    <div style={{padding:14}}>
      {cases.map(o => (
        <div key={o._id} onClick={() => onOpen(o._id)}
          style={{padding:14, marginBottom:10, backgroundColor:'white', borderRadius:10, border:'1px solid #e5e7eb', cursor:'pointer', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8}}>
            <div style={{flex:1}}>
              <div style={{fontFamily:'monospace', color:'#1e40af', fontWeight:700, fontSize:13}}>{o.caseCode || String(o._id).slice(-6).toUpperCase()}</div>
              <div style={{fontSize:15, fontWeight:700, color:'#111827', marginTop:2}}>{o.items || 'Lab case'}</div>
              <div style={{fontSize:12, color:'#6b7280'}}>
                Patient: {o.patientId?.name || '—'}
                {o.shade && <> · Shade {o.shade}</>}
                {o.expectedDate && <> · Due {new Date(o.expectedDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</>}
              </div>
            </div>
            <span style={{padding:'3px 10px', borderRadius:12, fontSize:11, fontWeight:700, color:'white', backgroundColor: statusColor[o.status] || '#6b7280'}}>
              {(o.subStage || o.status).toUpperCase()}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function CaseDetail({ token, caseId, onBack }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [working, setWorking] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/${token}/cases/${caseId}`);
      setData(r.data);
      setTimeout(() => scrollRef.current?.scrollTo({top:9e9, behavior:'smooth'}), 60);
    } catch (e) { alert('Failed to load case'); }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [caseId]);

  const send = async () => {
    if (!text.trim() && !files.length) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('text', text);
      files.forEach(f => fd.append('files', f));
      await axios.post(`${API}/${token}/cases/${caseId}/messages`, fd);
      setText(''); setFiles([]);
      await load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
    finally { setSending(false); }
  };

  const setStage = async (stage) => {
    if (!window.confirm(`Mark as ${stage.toUpperCase()}? The clinic will be notified.`)) return;
    setWorking(true);
    try {
      await axios.post(`${API}/${token}/cases/${caseId}/substage`, { stage });
      await load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
    finally { setWorking(false); }
  };

  if (!data) return <div style={centered}>Loading case…</div>;

  const { order, messages } = data;
  const scans = Array.isArray(order.scanImportIds) ? order.scanImportIds : [];

  const stageBtns = [
    { key:'accepted',  label:'✓ Accepted',  color:'#3b82f6' },
    { key:'working',   label:'🛠 Working',   color:'#6366f1' },
    { key:'finishing', label:'✨ Finishing', color:'#8b5cf6' },
    { key:'sent',      label:'📦 Sent',      color:'#10b981' },
  ];

  return (
    <div style={{display:'flex', flexDirection:'column', height:'calc(100vh - 66px)'}}>
      <div style={{padding:12, backgroundColor:'white', borderBottom:'1px solid #e5e7eb'}}>
        <button onClick={onBack} style={{background:'none', border:'none', color:'#6366f1', fontSize:13, fontWeight:600, cursor:'pointer', padding:0, marginBottom:8}}>← All cases</button>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10}}>
          <div>
            <div style={{fontFamily:'monospace', color:'#1e40af', fontWeight:700, fontSize:12}}>{order.caseCode}</div>
            <div style={{fontSize:16, fontWeight:700}}>{order.items}</div>
            <div style={{fontSize:12, color:'#6b7280'}}>
              {order.patientId?.name} {order.shade && <> · Shade {order.shade}</>} {order.treatmentId?.toothNumber && <> · Tooth #{order.treatmentId.toothNumber}</>}
            </div>
          </div>
          <span style={{padding:'3px 10px', borderRadius:12, fontSize:11, fontWeight:700, color:'white', backgroundColor: '#3b82f6', whiteSpace:'nowrap'}}>
            {(order.subStage || order.status).toUpperCase()}
          </span>
        </div>

        {/* Status buttons */}
        <div style={{display:'flex', gap:6, flexWrap:'wrap', marginTop:10}}>
          {stageBtns.map(b => (
            <button key={b.key} onClick={() => setStage(b.key)} disabled={working}
              style={{flex:'1 1 auto', padding:'10px 12px', backgroundColor:b.color, color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:working?0.5:1}}>
              {b.label}
            </button>
          ))}
        </div>

        {/* Scan downloads */}
        {scans.length > 0 && (
          <div style={{marginTop:10, padding:8, backgroundColor:'#eff6ff', borderRadius:8, borderLeft:'3px solid #3b82f6'}}>
            <div style={{fontSize:11, fontWeight:700, color:'#1e40af', marginBottom:4}}>📦 IOS SCANS FROM CLINIC</div>
            {scans.map(s => (
              <a key={s._id} href={`${API}/${token}/scans/${s._id}/download`}
                style={{display:'block', fontSize:13, color:'#1e40af', textDecoration:'none', padding:'4px 0'}}>
                🦷 {s.fileName} ({Math.round((s.sizeBytes||0)/1024)} KB) ↓
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{flex:1, overflowY:'auto', padding:14, backgroundColor:'#f9fafb'}}>
        {messages.length === 0 && (
          <div style={{textAlign:'center', color:'#9ca3af', padding:30, fontSize:13}}>
            No messages yet. Say hi to the clinic!
          </div>
        )}
        {messages.map(m => (
          <div key={m._id} style={{display:'flex', justifyContent: m.direction === 'lab_to_clinic' ? 'flex-end' : 'flex-start', marginBottom:8}}>
            <div style={{maxWidth:'80%', padding:'8px 12px', borderRadius:12, backgroundColor: m.direction === 'lab_to_clinic' ? '#dcfce7' : 'white', border:'1px solid ' + (m.direction === 'lab_to_clinic' ? '#86efac' : '#e5e7eb')}}>
              {m.senderName && <div style={{fontSize:10, color:'#6366f1', fontWeight:700, marginBottom:2}}>{m.senderName}</div>}
              {m.text && <div style={{fontSize:14, color:'#111827', whiteSpace:'pre-wrap'}}>{m.text}</div>}
              {Array.isArray(m.attachments) && m.attachments.map((a, i) => <AttachmentBubble key={i} a={a}/>)}
              <div style={{fontSize:10, color:'#9ca3af', marginTop:4, textAlign:'right'}}>{new Date(m.createdAt).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</div>
            </div>
          </div>
        ))}
      </div>

      {/* File preview chips */}
      {files.length > 0 && (
        <div style={{padding:'6px 10px', borderTop:'1px solid #e5e7eb', display:'flex', gap:6, flexWrap:'wrap', backgroundColor:'#fafafa'}}>
          {files.map((f, i) => (
            <div key={i} style={{padding:'4px 8px', backgroundColor:'white', border:'1px solid #e5e7eb', borderRadius:14, fontSize:12}}>
              {f.name} <button onClick={() => setFiles(p => p.filter((_, j) => j !== i))} style={{background:'none', border:'none', color:'#ef4444', cursor:'pointer'}}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Composer */}
      <div style={{padding:10, borderTop:'1px solid #e5e7eb', display:'flex', gap:8, alignItems:'flex-end', backgroundColor:'white'}}>
        <button onClick={() => fileRef.current?.click()} style={{background:'white', border:'1px solid #e5e7eb', borderRadius:8, padding:10, cursor:'pointer'}}>📎</button>
        <input ref={fileRef} type="file" multiple style={{display:'none'}} accept="image/*,.stl,.obj,.ply,.zip,.pdf"
          onChange={e => { setFiles(p => [...p, ...Array.from(e.target.files || [])]); e.target.value=''; }}/>
        <textarea value={text} onChange={e => setText(e.target.value)}
          placeholder="Reply to clinic…"
          style={{flex:1, padding:'10px 12px', borderRadius:8, border:'1px solid #e5e7eb', fontSize:14, fontFamily:'inherit', resize:'none'}}
          rows={2}/>
        <button onClick={send} disabled={sending || (!text.trim() && !files.length)}
          style={{padding:'12px 16px', backgroundColor:'#3b82f6', color:'white', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700}}>
          Send
        </button>
      </div>
    </div>
  );
}

function AttachmentBubble({ a }) {
  const url = a.url.startsWith('http') ? a.url : `/${a.url}`;
  if (a.kind === 'photo') {
    return <a href={url} target="_blank" rel="noreferrer" style={{display:'block', marginTop:6}}>
      <img src={url} alt={a.fileName} style={{maxWidth:220, borderRadius:8, border:'1px solid #e5e7eb'}}/>
    </a>;
  }
  return <a href={url} download={a.fileName}
    style={{display:'flex', alignItems:'center', gap:6, marginTop:6, padding:'6px 10px', backgroundColor:'#f3f4f6', borderRadius:8, textDecoration:'none', color:'#374151', fontSize:12}}>
    {a.kind === 'scan' ? '🦷' : '📄'} {a.fileName} ↓
  </a>;
}

const shell  = { minHeight:'100vh', backgroundColor:'#f3f4f6', display:'flex', flexDirection:'column' };
const hdr    = { padding:'14px 18px', backgroundColor:'#0f172a', display:'flex', justifyContent:'space-between', alignItems:'center' };
const centered = { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:20, backgroundColor:'#f3f4f6' };
